import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CalendarMonthOutlined, LockOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useEarningsCalendar,
  EarningsWeekKey,
} from "../../../../hooks/useEarningsCalendar";
import { useTouchLayout } from "../../../../hooks/useTouchLayout";
import EarningsDayColumn from "../../EarningsCalendar/EarningsDayColumn";
import EarningsDayModal from "../../EarningsCalendar/EarningsDayModal";
import CalendarSkeleton from "../../EarningsCalendar/CalendarSkeleton";
import {
  toBoardDays,
  toVisibleDay,
  todayKey,
} from "../../EarningsCalendar/earningsCalendarSelection";
import { touchHitAreaSx } from "../../EarningsCalendar/touchHitArea";
import {
  focusRingSx,
  panelBorder,
  panelTitleSx,
  riseIn,
  sectionHeaderSx,
} from "../../overviewPanel";
import { hapticSelection } from "../../../../lib/haptics";
import { tonalTokens } from "../../../polish/tokens";

// The market's reporting week. Everything here comes from the nightly mirror
// via one request — no per-company fetching anywhere on this surface.
//
// The calendar is MEMBER content: a free user gets the locked panel INSTEAD of
// the board, not beneath a teaser of it — a handful of rows was never enough
// to plan a week around, and half a board reads as a broken one.

const WEEK_OPTIONS: { key: EarningsWeekKey; label: string }[] = [
  { key: "current", label: "This week" },
  { key: "next", label: "Next week" },
];

// $10B is roughly the line between "a name most people know" and the long
// tail. The trimming itself lives in toBoardDays.
const LARGE_CAP_FLOOR = 10_000_000_000;
const SIZE_OPTIONS: { key: "all" | "large"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "large", label: "$10B+" },
];

const PILL_HEIGHT = 30;

// The tonal segmented control (TONAL_DESIGN_SYSTEM.md §7): plain labels, no
// frame, one soft green pill on the selected label.
const pillGroupSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.25,
  flexShrink: 0,
} as const;

const pillSx = (
  isDark: boolean,
  selected: boolean,
  secondary: string,
  primary: string,
) => {
  const tokens = tonalTokens(isDark);
  return {
    all: "unset",
    ...focusRingSx,
    cursor: "pointer",
    fontFamily: "inherit",
    fontVariantNumeric: "tabular-nums",
    fontSize: "0.8125rem",
    fontWeight: 500,
    letterSpacing: "0.01em",
    color: selected ? tokens.gain : secondary,
    px: 1.5,
    height: PILL_HEIGHT,
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "999px",
    backgroundColor: selected ? alpha(tokens.gain, 0.12) : "transparent",
    transition: "color 150ms ease, background-color 150ms ease",
    WebkitTapHighlightColor: "transparent",
    "@media (hover: hover)": {
      "&:hover": { color: selected ? tokens.gain : primary },
    },
  } as const;
};

const CalendarTab: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();

  const [week, setWeek] = useState<EarningsWeekKey>("current");
  const [size, setSize] = useState<"all" | "large">("all");
  // Kept apart from `isDayOpen` so the day stays on screen while the modal
  // fades out — clearing it on close blanked the header mid-transition.
  const [openDate, setOpenDate] = useState<string | null>(null);
  const [isDayOpen, setIsDayOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useEarningsCalendar(week);

  // Skeleton-first gate: with a warm cache paint nothing rather than flashing
  // a skeleton for one frame.
  const [contentReady, setContentReady] = useState(false);
  useEffect(() => {
    let inner: number | undefined;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setContentReady(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner !== undefined) cancelAnimationFrame(inner);
    };
  }, []);

  const today = todayKey();

  const weekDays = useMemo(() => {
    if (!data) return [];
    return toBoardDays(data, size === "large" ? LARGE_CAP_FLOOR : null);
  }, [data, size]);

  // Trimmed once per change rather than inline in the map, so the memoised
  // columns see the same day object between renders.
  const visibleDays = useMemo(
    () => weekDays.map((day) => toVisibleDay(day)),
    [weekDays],
  );

  const emptyLabel =
    size === "large" ? "No $10B+ companies reporting" : "No earnings scheduled";

  const openDay = useMemo(
    () => weekDays.find((day) => day.date === openDate) ?? null,
    [weekDays, openDate],
  );

  const handleOpenDay = useCallback((date: string) => {
    setOpenDate(date);
    setIsDayOpen(true);
  }, []);

  if (!contentReady && data) return null;

  // The skeleton sits inside the same padded, headed frame as the board, so
  // nothing jumps when the data lands.
  const showSkeleton = !data && !isError && (!contentReady || isLoading);

  const locked = data?.locked === true;
  const showBoard = !!data && !locked && visibleDays.length > 0;
  // Nothing withheld means nothing to sell: a quiet week for a free user is
  // just a quiet week, and reads as one.
  const showLocked = !!data && locked && data.meta.totalEvents > 0;
  const showEmptyWeek = !!data && !showBoard && !showLocked;
  const weekLabel = week === "current" ? "this week" : "next week";

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{ ...sectionHeaderSx, ...riseIn(0), flexWrap: "wrap", gap: 1.5 }}
      >
        <CalendarMonthOutlined sx={{ fontSize: 14, color: "#22c55e" }} />
        <Typography sx={{ ...panelTitleSx, color: theme.palette.text.primary }}>
          Earnings calendar
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* The free tier's body is already trimmed to a handful, so a size
            filter could only ever empty it. */}
        {!data?.locked && (
          <Box sx={pillGroupSx}>
            {SIZE_OPTIONS.map((option) => (
              <Box
                key={option.key}
                component="button"
                aria-pressed={size === option.key}
                onClick={() => {
                  if (size === option.key) return;
                  hapticSelection();
                  setSize(option.key);
                }}
                sx={{
                  ...pillSx(
                    isDark,
                    size === option.key,
                    theme.palette.text.secondary,
                    theme.palette.text.primary,
                  ),
                  ...(isTouch && touchHitAreaSx(PILL_HEIGHT)),
                }}
              >
                {option.label}
              </Box>
            ))}
          </Box>
        )}
        <Box sx={pillGroupSx}>
          {WEEK_OPTIONS.map((option) => (
            <Box
              key={option.key}
              component="button"
              aria-pressed={week === option.key}
              onClick={() => {
                if (week === option.key) return;
                hapticSelection();
                setWeek(option.key);
              }}
              sx={{
                ...pillSx(
                  isDark,
                  week === option.key,
                  theme.palette.text.secondary,
                  theme.palette.text.primary,
                ),
                ...(isTouch && touchHitAreaSx(PILL_HEIGHT)),
              }}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      </Box>

      {showSkeleton && <CalendarSkeleton />}

      {isError && !data && (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              color: theme.palette.text.secondary,
              mb: 1.5,
            }}
          >
            The earnings calendar is unavailable right now.
          </Typography>
          <Typography
            component="button"
            onClick={() => refetch()}
            sx={{
              all: "unset",
              ...focusRingSx,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#22c55e",
              "&:hover": { color: "#16a34a" },
            }}
          >
            Try again
          </Typography>
        </Box>
      )}

      {showEmptyWeek && (
        <Box
          sx={{
            ...riseIn(70),
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            minHeight: 160,
            borderRadius: 1.5,
            border: `1px solid ${panelBorder(isDark)}`,
          }}
        >
          <CalendarMonthOutlined
            sx={{ fontSize: 22, color: theme.palette.text.disabled }}
          />
          <Typography
            sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}
          >
            No companies reporting this week.
          </Typography>
        </Box>
      )}

      {showBoard && (
        <Box
          sx={{
            ...riseIn(70),
            display: "grid",
            // Each day carries a session pair, so it needs roughly twice the
            // width it would as a single list — the column counts step up
            // more slowly than a plain day grid would.
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "repeat(2, minmax(0, 1fr))",
              lg: `repeat(${Math.min(visibleDays.length, 3)}, minmax(0, 1fr))`,
              xl: `repeat(${Math.min(visibleDays.length, 5)}, minmax(0, 1fr))`,
            },
            gap: { xs: 2.5, md: 3 },
            alignItems: "start",
          }}
        >
          {visibleDays.map((day) => (
            <EarningsDayColumn
              key={day.date}
              day={day}
              isToday={day.date === today}
              emptyLabel={emptyLabel}
              onOpenDay={handleOpenDay}
            />
          ))}
        </Box>
      )}

      {showLocked && data && (
        <Box
          component="button"
          onClick={() => {
            hapticSelection();
            navigate("/start-free-trial");
          }}
          sx={{
            all: "unset",
            ...riseIn(70),
            ...focusRingSx,
            boxSizing: "border-box",
            // A card, not a banner: stretched the full width of a desktop
            // board it read as a dead rail rather than something to press.
            width: "100%",
            maxWidth: 420,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 4,
            cursor: "pointer",
            fontFamily: "inherit",
            borderRadius: 1.5,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.02)",
            border: `1px solid ${panelBorder(isDark)}`,
            WebkitTapHighlightColor: "transparent",
            transition: "border-color 0.2s ease",
            "&:hover": { borderColor: "#22c55e" },
            "&:active": { transform: "scale(0.99)" },
          }}
        >
          <LockOutlined
            sx={{ fontSize: 22, color: theme.palette.text.secondary }}
          />
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {data.meta.totalEvents} companies reporting {weekLabel}
          </Typography>
          <Typography
            sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}
          >
            See the full calendar with Insight Pro
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: "0.6875rem",
              fontWeight: 600,
              color: "#22c55e",
              border: "1px solid #22c55e",
              borderRadius: 0.75,
              px: 1,
              lineHeight: "20px",
            }}
          >
            Pro
          </Typography>
        </Box>
      )}

      <EarningsDayModal
        day={openDay}
        open={isDayOpen}
        onClose={() => setIsDayOpen(false)}
      />
      <Box sx={{ height: 24 }} />
    </Box>
  );
};

export default CalendarTab;
